import { Link } from 'react-router-dom';
import { Layout } from '@/components/Layout';

interface Section {
  n: string;
  title: string;
  paras?: string[];
  bullets?: string[];
}

const LAST_UPDATED = '2026-06-24';

const SECTIONS: Section[] = [
  {
    n: '1',
    title: 'Who We Are',
    paras: [
      'This Privacy Policy explains how GRID LLC, a Wyoming, USA limited liability company ("GRID", "we", "us"), collects and uses personal information when you use the GRID Support portal (the "Service"). By using the Service, you agree to this Policy.',
    ],
  },
  {
    n: '2',
    title: 'Information We Collect',
    paras: ['We collect the following categories of information:'],
    bullets: [
      'Account information — when you sign in with Google, we receive your name, email address, and profile photo.',
      'Affiliate information — if you join the affiliate program, your X (Twitter) username and XRPL wallet address.',
      'Support content — the tickets, messages, and file attachments you submit, including anything you choose to include in them.',
      'Usage and technical data — basic log and device information generated when you use the Service (e.g. timestamps, browser type).',
      'Authentication data — session tokens stored in your browser to keep you signed in.',
    ],
  },
  {
    n: '3',
    title: 'How We Use Information',
    paras: ['We use personal information to:'],
    bullets: [
      'Provide, operate, and secure the Service and authenticate your account.',
      'Create and manage support tickets and communicate with you about them.',
      'Operate the affiliate program, attribute referrals, and calculate and send payouts.',
      'Comply with legal, tax, and accounting obligations.',
      'Detect, prevent, and address fraud, abuse, and security issues.',
    ],
  },
  {
    n: '4',
    title: 'Legal Bases (EEA/UK Users)',
    paras: [
      'Where the GDPR or UK GDPR applies, we process personal data on the bases of performance of a contract (providing the Service and affiliate program), our legitimate interests (security, improving the Service), your consent where required, and compliance with legal obligations.',
    ],
  },
  {
    n: '5',
    title: 'How We Share Information',
    paras: [
      'We do not sell your personal information. We share it only with service providers that help us operate the Service, when required by law, or in connection with a business transfer.',
    ],
    bullets: [
      'Google / Firebase — authentication, database, hosting, and serverless functions.',
      'ClickUp — optional syncing of support tickets to our internal task system.',
      'Discord — optional operational notifications to our support team.',
      'Legal and safety — to comply with law, enforce our terms, or protect rights and safety.',
    ],
  },
  {
    n: '6',
    title: 'Blockchain / XRPL Payments',
    paras: [
      'Affiliate payouts are made on the XRP Ledger (XRPL), a public blockchain. Your wallet address and any transaction we send to it are recorded on a public, immutable ledger that we do not control and cannot alter or delete. Provide only a wallet address you are comfortable using for this purpose.',
    ],
  },
  {
    n: '7',
    title: 'Cookies and Local Storage',
    paras: [
      'We use cookies and browser local storage strictly to keep you signed in and to operate core functionality. We do not use third-party advertising or cross-site tracking cookies.',
    ],
  },
  {
    n: '8',
    title: 'Data Retention',
    paras: [
      'We retain personal information for as long as your account is active and as needed to provide the Service, and thereafter as required to meet legal, tax, accounting, or dispute-resolution obligations. Affiliate and payout records may be retained longer to satisfy financial and tax requirements.',
    ],
  },
  {
    n: '9',
    title: 'Your Rights',
    paras: [
      'Depending on where you live, you may have the right to access, correct, delete, or receive a copy of your personal information, to object to or restrict certain processing, and to withdraw consent. California residents may request to know or delete personal information and are entitled to non-discrimination for exercising these rights; we do not sell personal information. To exercise any right, contact us using the details below. We may need to verify your identity before responding.',
    ],
  },
  {
    n: '10',
    title: 'Security',
    paras: [
      'We use industry-standard measures such as encryption in transit and access controls to protect personal information. No method of transmission or storage is completely secure, and we cannot guarantee absolute security.',
    ],
  },
  {
    n: '11',
    title: 'International Data Transfers',
    paras: [
      'We operate in the United States, and our service providers may process data in the United States and other countries. Where required, we rely on appropriate safeguards for international transfers.',
    ],
  },
  {
    n: '12',
    title: 'Children',
    paras: [
      'The Service is not directed to children under 18, and we do not knowingly collect personal information from them. If you believe a child has provided us information, contact us and we will delete it.',
    ],
  },
  {
    n: '13',
    title: 'Changes to This Policy',
    paras: [
      'We may update this Policy from time to time. Material changes will be posted here with an updated date. Continued use of the Service after a change means you accept the updated Policy.',
    ],
  },
];

export function PrivacyPolicyPage() {
  return (
    <Layout>
      <div style={{ maxWidth: '760px', margin: '0 auto' }}>
        <Link to="/" style={{ color: 'var(--text-3)', textDecoration: 'none', fontFamily: 'var(--font-mono)', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          ← Back Home
        </Link>

        <div className="terminal-card" style={{ marginTop: '16px' }}>
          <div className="card-header">
            <span className="card-title">Privacy Policy</span>
          </div>
          <div className="card-body">
            <p className="prose prose--sm prose--muted">Last updated {LAST_UPDATED}</p>
            <div className="terminal-alert" style={{ background: 'var(--accent-dim)', border: '1px solid var(--border-gold)', color: 'var(--text-2)', fontSize: '11px' }}>
              Template document — have it reviewed by a qualified attorney before relying on it.
            </div>

            {SECTIONS.map((s) => (
              <div key={s.n} style={{ marginTop: '6px' }}>
                <div style={{ color: 'var(--accent)', fontWeight: 700, fontSize: '13px', marginBottom: '4px' }}>
                  {s.n}. {s.title}
                </div>
                {s.paras?.map((p, i) => (
                  <p key={i} className="prose prose--sm prose--muted">{p}</p>
                ))}
                {s.bullets && (
                  <ul className="rules-list" style={{ marginTop: '4px' }}>
                    {s.bullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}

            <p className="prose prose--sm" style={{ marginTop: '12px', color: 'var(--text)' }}>
              <strong>Contact:</strong> admin@gridservices.xyz
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
}
